<template v-if="visible">
	<div v-if="readonly" class="avatar-zone readonly-zone"  :style="style" draggable="false">
		<img :src="avatarUrl" alt="" class='avatarImg' />
	</div>
	<div v-else @drop.prevent="onDrop" :data-active="active" @mouseenter="mouseActive=true" @mouseleave="mouseActive=false"
			@dragenter.prevent="setActive" @dragover.prevent="setActive" v-bind="tabindexBinding()"
			@dragleave.prevent="setInactive" :class="[dropClass]" class="avatar-zone"
			:style="style" @keydown.ctrl.v="onClipboard" @keydown.meta.v="onClipboard">
			<sym-icon v-if="showCloseIcon" name="cross-circle-16-full" class="fi fileZoneCancelIcon" @click.prevent.stop="removeFile" />
			<sym-icon v-if="uploading" name="refresh" class="uploadingIcon upload-icon" />
			<img v-else-if="avatarExists" :src="avatarUrl" alt="" class='avatarImg pointer-area' @click.prevent.stop="onFileInput" draggable="false"/>
			<sym-icon v-else name="photo-add" class="upload-icon" @click="onFileInput"/>
			<input type="file" accept="image/*" capture="environment" ref="fileInputEl" @change="onInputChange" style="display: none;" />

			<span>{{ errorMessage }}</span>
	</div>
</template>
<script setup lang="ts">
	import {ref, computed} from 'vue';
	import SymIcon from "@fd/helpers/SymIcon.vue";

	interface AvatarFile {
		fileName?: string;
		contentType?: string;
		url?: string;
		data?: string;
	}

	const props = defineProps({
		visible: Boolean,
		readonly: Boolean,
		value: Object,
	});
	
	const MAX_SIZE=1048576; //1MB
	const avatar = defineModel<AvatarFile>();
	if (!avatar.value) {
		avatar.value = {fileName:null,contentType:null,data:null,url:null};
	}

		
	const touchable = false; //TODO

	const fileInputEl = ref(null);
	const errorMessage = ref("");
	const active = ref(false);
	const mouseActive = ref(false);
	const inActiveTimeout = ref(null);
	const uploading = ref(false);
	
	const avatarExists = computed(() => {
		return avatar.value && (avatar.value.data || avatar.value.url);
	});
	
	const avatarErrorClass = computed(() => {
		if (isError.value || errorClass.value) return "invalidAvatar";
		else return "";
	});

	const avatarUrl = computed(() => {
		if (avatar.value.data) {
			return "data:"+avatar.value.contentType+";base64, "+avatar.value.data;
		}
		return "";
	});
	
	const isError = computed(() => {
		return  errorMessage.value !== null && errorMessage.value !== undefined && errorMessage.value.length>0;
	});
	
	const showCloseIcon = computed(() => {
		return  !uploading.value && avatarExists.value && (touchable || mouseActive.value);
	});
	
	const dropClass = computed(() => {
		return active.value?"active-area":"";
	});

	function setActive(): void {
		active.value = true;
		clearTimeout(inActiveTimeout.value) // clear the timeout
	};
	
	function setInactive(): void {
		inActiveTimeout.value = setTimeout(() => {
			active.value = false;
		}, 200);
	}
	
	function removeFile(): void {
		avatar.value.fileName="";
		avatar.value.contentType="";
		avatar.value.url="";
		avatar.value.data=null;
		avatarExists.value=false;
	}
	
	function onInputChange(e): void {
		console.log("file input");
		setFile(e.target.files);
		e.target.value = null;
	}
	
	function onDrop(e): void {
		console.log("file drag&drop");
		setInactive();

		let dragFiles = e.dataTransfer.files; //drag&drop
		console.log("drag files:"+dragFiles.length);
		if (dragFiles !== null && dragFiles !== undefined && dragFiles.length>0) {
			setFile([...dragFiles]);
		}
		else {//error console log
			if (dragFiles !== null && dragFiles !== undefined) console.log("Undefined e.dataTransfer.files");
			else if (dragFiles.length===0) console.log("e.dataTransfer.files lenght is 0 - empty?");
			showError(dragError);
		}
	};
	
	function onFileInput(): void {
		fileInputEl.value.click();
	}
	
	function onClipboard(): void {
		console.log("on clipboard");
		navigator.permissions.query({name: "clipboard-read"}).then((permission)=>{
			if (permission.state === "granted" || permission.state === "prompt") {
				navigator.clipboard.read()
					.then(clipboardContents=> {
						var promises = [];
						for (const item of clipboardContents) {
							promises.push(asPromise(item));
						}
						Promise.all(promises).then((files) => {
							setFile(files);
						});
					})
					.catch((error) => {
						console.log("error reading clipboard:"+JSON.stringify(error));
						showError(JSON.stringify(error));
					});
			}
			else {
				console.log("Browser clipboard disabled");
				showError("Prohlížeč nepodporuje kopírování ze schránky.");
			}
		})
		.catch((error) => {
			console.log("error check permissions:"+JSON.stringify(error));
			showError("Chyba v prohlížeči:"+error.message);
		});
	}
	
	function validate(uploadedFile: File): boolean {
		errorMessage.value="";
		if (!uploadedFile.type.includes("image")) {
			showError("Chybný typ souboru");
			return false;
		}
		return true;
	}
	
	function setFile(newFiles: File[]): void {
		var uploadedFile: File = newFiles[0];
		console.log("File to upload:"+uploadedFile.name+ " with size:"+uploadedFile.size);
		// resetAvatar();
		
		if (validate(uploadedFile)) {
			uploading.value=true;
			avatar.value.fileName=uploadedFile.name;
			avatar.value.contentType=uploadedFile.type;

			let reader = new FileReader();
			reader.readAsArrayBuffer(uploadedFile);
			reader.onload = function() {
				avatar.value.data=window.btoa(bin2string(new Uint8Array(reader.result)));
				avatarExists.value=true;
				avatar.value.url="";
				uploading.value=false;
			};
		}
	}
	
	function asPromise(clipboardItem: ClipboardItem): Promise<File>
	{
		return new Promise((resolve, reject) => {
			//console.log("types:"+clipboardItem.types);
			const type = clipboardItem.types[0];
			//console.log("type:"+type);
			if (type==='text/plain') {
				reject(new TypeError("Vložte obrázek"));
			}
			else {
				clipboardItem.getType(type)
				.then(blob=>{
					resolve(blobToFile(blob));
				})
				.catch((error) => {
					reject(error);
				});
			}
		});
	}
	
	function bin2string(array): string {
		var result = "";
		for(var i = 0; i < array.length; ++i){
			result+= (String.fromCharCode(array[i]));
		}
		return result;
	}
	
	function showError(message: string): void {
		errorMessage.value=message;
	}
</script>

<style>
.avatarImg {
	margin: auto;
	max-height: 150px;
	max-width: 250px;
}

.avatar-zone {
	border: none;
	background-color: var(--bg);
	color: var(--fg);
	box-shadow: var(--input-shadow);
	border-radius: 3px;
	padding: var(--input-h-padding);
	display: flex;
    flex-direction: column;
    justify-content: space-evenly;
	align-items: center;
	transition: .2s ease;
	position: relative;
	box-sizing: border-box;
	min-height: 80px;
	min-width: 150px;
}

.avatar-zone:focus,
.avatar-zone:focus-visible {
	box-shadow: 0 0 2px 2px var(--outline-color);
	outline: none;
}

.readonly-zone {
	background-color: var(--input-disabled-bg) !important;
}

.pointer-area {
	cursor: pointer;
}

.avatar-zone label {
	cursor: pointer;
}

.active-area {
	background-color: var(--placeholder-fg) !important;
}

.upload-icon {
	font-size: 28px;
	opacity: 0.5;
	cursor: pointer;

	&:hover {
		opacity: 1;
		color: var(--link-fg-hover);
	}
}

.invalidAvatar {
	color: var(--error-fg);
    background-color: var(--error-bg);
}

</style>

